import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { supabase } from '@/services/supabase';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

type ForgotPasswordFormData = {
  email: string;
};

export const ForgotPasswordPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>();

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setIsLoading(true);
    setError(null);

    try {
      // Dynamically choose redirectTo based on environment
      const getRedirectTo = () => {
        if (
          window.location.hostname === 'localhost' ||
          window.location.hostname.startsWith('127.') ||
          window.location.hostname.endsWith('.vercel.app')
        ) {
          // Development or Vercel Preview
          return 'https://catch-of-the-day-webtest-aymorid-aymorids-projects.vercel.app/reset-password';
        }
        // Production
        return 'https://businesscharity.catchoftheday.io/reset-password';
      };

      const redirectTo = getRedirectTo();
      console.log('Setting password reset redirect to:', redirectTo);
      
      // Use Supabase directly as the single source of truth for authentication
      const { error } = await supabase.auth.resetPasswordForEmail(data.email, {
        redirectTo,
      });

      if (error) throw error;
      
      // Show success message
      setEmailSent(true);
      toast.success('Password reset email sent. Please check your inbox.');
      console.log('Password reset email sent successfully to:', data.email);
    } catch (err: any) {
      console.error('Password reset error:', err);
      setError(err.message || 'Failed to send password reset email');
      toast.error('Failed to send email: ' + (err.message || 'Unknown error'));
    } finally {
      setIsLoading(false);
    }
  };

  // If email is sent, show confirmation screen
  if (emailSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full px-6 py-8 bg-white shadow-md rounded-lg">
          <div className="text-center">
            <img
              src="/favicon.png"
              alt="Catch of the Day Logo"
              className="h-16 w-auto mx-auto mb-4"
            />
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">
              Check Your Email
            </h2>
            <div className="bg-green-50 text-green-700 p-4 rounded-md mt-4 mb-6 text-sm">
              We've sent a password reset link to your email address. Please check your inbox and follow the instructions.
            </div>
            <div className="mt-6">
              <Link
                to="/login"
                className="w-full inline-flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-sky-blue hover:bg-teal focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-blue transition-colors duration-200"
              >
                Return to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full px-6 py-8 bg-white shadow-md rounded-lg">
        <div className="text-center">
          <img
            src="/favicon.png"
            alt="Catch of the Day Logo"
            className="h-16 w-auto mx-auto mb-4"
          />
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">
            Reset Your Password
          </h2>
          <p className="text-sm text-gray-600 mb-6">
            Enter your email address and we'll send you a link to reset your password.
          </p>
        </div>

        <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-dark-gray mb-1">
              Email Address
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 text-dark-gray focus:outline-none focus:ring-2 focus:ring-sky-blue focus:border-sky-blue transition duration-150 ease-in-out sm:text-sm"
              placeholder="you@example.com"
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address',
                },
              })}
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600 font-medium">{errors.email.message}</p>
            )}
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-100 p-3 rounded-md">
              {error}
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-sky-blue hover:bg-teal focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-blue transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Sending...' : 'Send Reset Link'}
            </button>
          </div>

          <div className="flex items-center justify-center">
            <div className="text-sm">
              <Link to="/login" className="font-medium text-sky-blue hover:text-teal transition-colors duration-200">
                Back to Login
              </Link>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
